import * as THREE from 'three';
import { G, M, TINT, HP, glowMat, mesh, plate, rib, mergeBones, stages, boneG } from './kit';
/* ============================================================
   NAMENLOS 無名 —— BOSS・五重對稱
   ============================================================

   剪影：五臂十五指・沒有一處二重對稱
   階段：'P1 五臂垂下・緩慢','P2 五臂水平展開・十五指全張','P3 收成平躺的五芒星・旋轉'

   由 `tools/port-bosses.mjs` 從 `enemy_sample_ani.html` 機械移植，
   **不要手改這個檔**：要改造型請改原型再重跑移植器，否則 1:1 就斷了。
   驗證：`node tools/port-bosses.mjs --check`。
   ⚠️ 比對只守得住數字與字串，**動畫對不對只能實看**（圖鑑的開發模式）。 */


/* ══════════════════════════════════════════════════════════════
   BOSS 18–23 · 深層 —— 幾何機甲風的宇宙恐怖。

   設計難題：宇宙恐怖要的是有機、非歐、無法理解的形體，
   而這個專案只有五種硬邊 primitive 加平面著色。兩者是對立的。
   解法不是把幾何弄軟，是找出「剛體幾何做得到的錯誤感」：

     1. 錯誤的對稱數 —— 五重、七重、十三重。機械是二或四重對稱，
        五重連鋪不滿平面，看久了會不舒服。
     2. 沒有骨架 —— 板件浮在該有關節的地方，中間什麼都沒有。
     3. 不可能的幾何 —— 兩個實心環互相穿透。
     4. 不完整的存在 —— 只有一隻手臂進到場裡，本體從頭到尾沒出現。
     5. 不動 —— 完全靜止的東西，只在你不注意時瞬間換方向（無插值）。
     6. 那曾經是我們的 —— 用玩家熟悉的機甲語彙當宿主。

   顏色紀律不變：這六隻一律以紫（範圍）為主 ——
   「地面本身不安全」正好是宇宙恐怖最準確的機制翻譯。
   ══════════════════════════════════════════════════════════════ */

/* ── BOSS-18 NAMENLOS 無名 ── 全身沒有一處是二或四重對稱。
   五片核甲、五條臂、每條臂三指共十五指、五顆眼。
   五重對稱鋪不滿平面，所以它永遠看起來「差一點就對稱了」。
   P1 五臂垂下、緩慢。P2 五臂水平展開、十五指全張。
   P3 五臂收成一個平躺的五芒星，整體旋轉 —— 從生物變成刃。 */
var NMA = 5;
export function buildNamenlos(){
  var GLV = glowMat(TINT.vio.hex, 2.4);
  var root = new THREE.Group(), arms = [], eyes = [], i, j;
  var core = boneG(root, 0, 5.00, 0);
  core.add(mesh(G.oct, M.dark, [3.00,3.40,3.00], [0,0,0]));
  for (i = 0; i < NMA; i++){
    var a = i*Math.PI * 2/NMA;
    core.add(plate(M.armor, 1.90, 2.60, 0.55,
      [Math.sin(a)*1.30, 0.30, Math.cos(a)*1.30], 0, 0, 0));
    core.add(rib(M.armor, 0.14, 2.40, 0.14,
      [Math.sin(a)*1.62, 0.30, Math.cos(a)*1.62]));
    var ev = mesh(G.oct, GLV, [0.62,0.62,0.62],
      [Math.sin(a)*1.66, 1.30, Math.cos(a)*1.66]);
    ev.userData.noMerge = 1; core.add(ev); eyes.push(ev);
  }
  var vd = mesh(G.oct, M.void, [1.70,1.90,1.70], [0, 0, 0]);
  vd.userData.noMerge = 1; core.add(vd);
  for (i = 0; i < NMA; i++){
    var b = i*Math.PI * 2/NMA + 0.62;                       // 刻意跟核甲錯開半格
    var ar = boneG(core, Math.sin(b)*1.50, -1.20, Math.cos(b)*1.50);
    ar.rotation.y = b;
    ar.add(mesh(G.cyl, M.armor, [0.62,3.20,0.62], [0,-1.55,0]));
    ar.add(mesh(G.tor, M.trim,  [1.10,1.10,0.50], [0,-3.10,0], [HP,0,0]));
    var hd = boneG(ar, 0, -3.30, 0);
    for (j = 0; j < 3; j++){                        // 三指 × 五臂 = 十五
      var c = j*Math.PI * 2/3 + 0.4;
      hd.add(mesh(G.cone, M.armor, [0.34,1.60,0.34],
        [Math.sin(c)*0.42, -0.70, Math.cos(c)*0.42],
        [Math.PI - 0.30*Math.cos(c), 0, 0.30*Math.sin(c)]));
    }
    arms.push({ar:ar, hd:hd, b:b});
  }
  root.userData.rig = {core:core, arms:arms, eyes:eyes, vd:vd, GLV:GLV};
  return mergeBones(root);
}
export function updNamenlos(rig, o, dt){
  stages(rig, o, dt, 1.7);
  var s1 = rig.s1, s2 = rig.s2, i, A;
  rig.core.rotation.y += dt*(0.22 + 0.7*s1 + 3.0*s2);
  rig.core.rotation.x = 1.35*s2;                    // 三階段整顆平躺
  rig.core.position.y = 5.00 - 1.40*s2 + 0.20*Math.sin(o.t*0.8);
  for (i = 0; i < NMA; i++){
    A = rig.arms[i];
    A.ar.rotation.x = -1.32*s1 + 0.26*s2 + 0.14*Math.sin(o.t*1.3 + i*1.7)
                    - o.tel*0.30 + o.atk*0.55;
    A.ar.rotation.z = 0.20*Math.sin(o.t*0.9 + i*2.4)*(1-s2);
    A.hd.rotation.y += dt*(0.4 + 2.2*s1);
    A.hd.scale.setScalar(1 + 0.55*s1 + 0.30*o.tel);
  }
  for (i = 0; i < NMA; i++)
    rig.eyes[i].scale.setScalar(0.7 + 0.5*Math.sin(o.t*2.2 + i*1.26) + 0.9*o.tel + 0.4*s2);
  rig.vd.scale.setScalar(1 + 0.7*s1 + 0.5*s2);
  rig.GLV.emissiveIntensity = 2.4 + 2.8*o.tel + 6*o.atk*(1-o.atk) + 1.2*s2;
}